import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EditNavButton, BrandingEditingProvider, EditableText, useInlineEdit } from '@kevlarcon/pages';
import { applyBrandingSave, siteApi, MenuItem, SiteBranding } from '../api/site';
import { authApi, TenantBranding } from '../api/auth';
import { useIsLogoDark } from '../theme/logoGlow';
import { useAuth } from '../contexts/AuthContext';
import { useBrandPreview } from '../contexts/BrandPreviewContext';

type Props = { onEditNav?: () => void; onEditFooter2Nav?: () => void; onEditLegalNav?: () => void };

/**
 * The tenant site footer. The two link columns are CMS-driven (the tenant's
 * `footer`/`footer_2` menus) the same way SiteHeader's primary nav draws from
 * `main` — an org admin adds, removes, and reorders footer links through the
 * same NavigationEditor, without an engineer hand-editing this file or a link
 * ever sitting dead at "#". The `legal` menu backs the small links row next to
 * the copyright line. Apply/Give stay fixed, same rationale as SiteHeader's
 * CTAs: they're built-in routes, not CMS pages. Brand and legal boilerplate
 * draw from the tenant's branding (name/description/address/phone/tax_id/
 * footer_heading/footer_tagline/footer_doxology) rather than a hardcoded
 * string, so a field left blank simply doesn't render instead of showing
 * another tenant's placeholder text.
 */
export default function SiteFooter({ onEditNav, onEditFooter2Nav, onEditLegalNav }: Props) {
  const queryClient = useQueryClient();
  const { isEditMode } = useInlineEdit();
  const { user, isSuperuser, isOrgAdmin, isPartner, logout } = useAuth();

  // Where "Dashboard" lands depends on which console the caller owns. This
  // footer only renders on a tenant's site, where a system admin counts as an
  // org admin, so they get the tenant console; /superadmin is the fallback.
  const dashboardPath = isOrgAdmin
    ? '/settings/donations'
    : isSuperuser
      ? '/superadmin'
      : isPartner
        ? '/partners/dashboard'
        : null;

  const { data: menu } = useQuery<MenuItem[]>({
    queryKey: ['site-menu', 'footer'],
    queryFn: () => siteApi.getMenu('footer'),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const { data: menu2 } = useQuery<MenuItem[]>({
    queryKey: ['site-menu', 'footer_2'],
    queryFn: () => siteApi.getMenu('footer_2'),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const { data: legalMenu } = useQuery<MenuItem[]>({
    queryKey: ['site-menu', 'legal'],
    queryFn: () => siteApi.getMenu('legal'),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const { data: branding } = useQuery<TenantBranding>({
    queryKey: ['tenant_branding'],
    queryFn: authApi.getBranding,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // See SiteHeader for why this doesn't come through props.
  const preview = useBrandPreview();

  const brandingValues: SiteBranding = {
    title: branding?.name ?? '',
    logo: branding?.logo ?? '',
    logo_alt: branding?.logo_alt ?? '',
    header_logo_height: preview.header_logo_height !== undefined ? preview.header_logo_height : (branding?.header_logo_height ?? null),
    footer_logo: branding?.footer_logo ?? '',
    footer_logo_alt: branding?.footer_logo_alt ?? '',
    footer_logo_height: preview.footer_logo_height !== undefined ? preview.footer_logo_height : (branding?.footer_logo_height ?? null),
    title_font_size: preview.title_font_size !== undefined ? preview.title_font_size : (branding?.title_font_size ?? null),
    title_text_transform: preview.title_text_transform !== undefined ? preview.title_text_transform : (branding?.title_text_transform ?? ''),
    title_bold: preview.title_bold !== undefined ? preview.title_bold : (branding?.title_bold ?? false),
    title_italic: preview.title_italic !== undefined ? preview.title_italic : (branding?.title_italic ?? false),
    title_max_width: branding?.title_max_width ?? null,
    footer_heading: branding?.footer_heading ?? '',
    footer_tagline: branding?.footer_tagline ?? '',
    footer_doxology: branding?.footer_doxology ?? '',
  };

  const titleStyle: React.CSSProperties = {
    ...(brandingValues.title_font_size ? { fontSize: `${brandingValues.title_font_size}px` } : {}),
    ...(brandingValues.title_text_transform ? { textTransform: brandingValues.title_text_transform } : {}),
    ...(brandingValues.title_bold ? { fontWeight: 700 } : {}),
    ...(brandingValues.title_italic ? { fontStyle: 'italic' } : {}),
    ...(brandingValues.title_max_width
      ? { maxWidth: `${brandingValues.title_max_width}px`, display: 'inline-block', whiteSpace: 'normal' }
      : {}),
  };

  // Automatic: a dark logo would otherwise disappear against the dark footer
  // background, so it earns a soft light glow with no admin action needed —
  // see useIsLogoDark. footer_logo already mirrors the header logo when the
  // admin hasn't set a footer-specific override (OrganizationBrandingProvider
  // resolves that fallback server-side).
  const isLogoDark = useIsLogoDark(brandingValues.footer_logo, Boolean(brandingValues.footer_logo));

  const hasOffice = Boolean(branding?.address || branding?.phone);

  return (
    <footer className="site-footer">
      <div className="wrap">
        <div className="ft-grid">
          <div>
            {brandingValues.footer_logo && (
              <img
                src={brandingValues.footer_logo}
                alt={brandingValues.footer_logo_alt}
                className={`brand-logo${isLogoDark ? ' brand-logo-glow' : ''}`}
                style={brandingValues.footer_logo_height ? { height: `${brandingValues.footer_logo_height}px` } : undefined}
              />
            )}
            <span className="brand-title" style={titleStyle}>{brandingValues.title}</span>
            {branding?.description && <p className="ft-tag">{branding.description}</p>}
          </div>
          <div>
            <h4>
              Explore
              {onEditNav && <EditNavButton onClick={onEditNav} />}
            </h4>
            <ul>
              <li><a href="/apply" data-testid="site-footer-apply-link">Apply</a></li>
              <li><a href="/donate" data-testid="site-footer-donate-link">Give</a></li>
              {(menu ?? []).map((item) => (
                <li key={item.id}>
                  <a
                    href={item.url}
                    className={item.css_class || undefined}
                    data-testid={`site-footer-${item.id}-link`}
                  >
                    {item.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4>
              Resources
              {onEditFooter2Nav && <EditNavButton onClick={onEditFooter2Nav} />}
            </h4>
            <ul>
              {(menu2 ?? []).map((item) => (
                <li key={item.id}>
                  <a
                    href={item.url}
                    className={item.css_class || undefined}
                    data-testid={`site-footer-2-${item.id}-link`}
                  >
                    {item.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            {isEditMode ? (
              <BrandingEditingProvider
                endpoint="/site/branding/"
                values={brandingValues}
                active
                onSaved={(saved) => queryClient.setQueryData<TenantBranding>(
                  ['tenant_branding'],
                  (old) => (old ? applyBrandingSave(old, saved) : old),
                )}
              >
                <EditableText value={brandingValues.footer_heading} path={['footer_heading']} as="h4" />
                <EditableText
                  value={brandingValues.footer_tagline}
                  path={['footer_tagline']}
                  as="p"
                  style={{ fontSize: '14px', opacity: 0.82, marginTop: 0 }}
                />
              </BrandingEditingProvider>
            ) : (
              <>
                <h4>{brandingValues.footer_heading}</h4>
                {brandingValues.footer_tagline && (
                  <p style={{ fontSize: '14px', opacity: 0.82, marginTop: 0 }}>{brandingValues.footer_tagline}</p>
                )}
              </>
            )}
            <form className="nl-form" onSubmit={(e) => e.preventDefault()}>
              <input type="email" placeholder="your@email.com" />
              <button type="submit">Subscribe</button>
            </form>
            {hasOffice && (
              <div style={{ marginTop: 22, fontSize: 13, opacity: 0.7 }}>
                <strong
                  style={{
                    color: 'var(--gold-pale)',
                    fontFamily: 'var(--serif-sc)',
                    letterSpacing: '0.16em',
                    fontSize: 11,
                  }}
                >
                  OFFICE
                </strong>
                <br />
                {branding?.address && (
                  <span style={{ whiteSpace: 'pre-line' }}>
                    {branding.address}
                    <br />
                  </span>
                )}
                {branding?.phone}
              </div>
            )}
          </div>
        </div>
        <div className="ft-legal">
          <span>
            © {new Date().getFullYear()} {branding?.name}. A 501(c)(3) Scholarship Granting
            Organization.{branding?.tax_id ? ` EIN ${branding.tax_id}.` : ''}
          </span>
          {branding?.footer_doxology && <span className="doxology">{branding.footer_doxology}</span>}
          <span className="legal-links">
            {(legalMenu ?? []).map((item) => (
              <a
                key={item.id}
                href={item.url}
                className={item.css_class || undefined}
                data-testid={`site-footer-legal-${item.id}-link`}
              >
                {item.text}
              </a>
            ))}
            {user ? (
              <>
                {dashboardPath && (
                  <a href={dashboardPath} className="ft-auth-link" data-testid="site-footer-dashboard-link">Dashboard</a>
                )}
                <button
                  type="button"
                  className="ft-auth-btn"
                  onClick={async () => { await logout(); window.location.assign('/'); }}
                  data-testid="site-footer-logout-button"
                >
                  Logout
                </button>
              </>
            ) : (
              <a href="/login" className="ft-auth-link" data-testid="site-footer-login-link">Login</a>
            )}
            {onEditLegalNav && <EditNavButton onClick={onEditLegalNav} />}
          </span>
        </div>
      </div>
    </footer>
  );
}
